import { FormattedDate, FormattedMessage, useIntl } from "react-intl"
import { useSelector } from "react-redux"
import { getCompanyCurrencyCode } from "#/redux/reducers/companyInfo"
import { ReimbursementDescriptionGroup, ReimbursementDescriptionList } from "./ReimbursementDescriptionList"
import React, { useEffect, useState, useMemo } from "react"
// eslint-disable-next-line no-restricted-imports
import { Icon } from "@carrotfertility/carotene" // please do not import from @carrotfertility/carotene after eslint rule added on 3/20/25
import { getIsCarrotCardEligible } from "#/redux/reducers/userInfo"
import { useReimbursements } from "#/services/user/hooks/useReimbursements"
import { getPiiProps } from "../../../../services/tracking"
import moment from "moment/moment"
import { ReimbursementAmount } from "./ReimbursementAmounts"
import { ExpenseState } from "../../../../utils/ExpenseState"
import { reportErrorMessage } from "../../../../utils/ErrorReporting"
import { ReimbursementsListDeductibleString } from "./reimbursement-status-lists/ReimbursementsListDeductibleString"
import { ReimbursementsListPaidExpenseState } from "./reimbursement-status-lists/ReimbursementsListPaidExpenseState"
import { Attachments } from "../upload/Attachments"
import { useToggle } from "../../../../utils/Hooks"
import EmployeeOnlyModalContainer from "../../../employee-only-containers/EmployeeOnlyModalContainer"
import { UploadAdditionalDocumentsFlow } from "../../../upload-additional-documents"
import { useCurrentUser } from "../../../context/user/UserContext"
import { useFlags } from "launchdarkly-react-client-sdk"
import {
  Alert,
  Avatar,
  Box,
  Button,
  CloseIcon,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  IconWithBackground,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  BanknoteIcon,
  HospitalIcon,
  CreditCardIcon
} from "@carrotfertility/carotene-core"
import { UpdateBankInfoModal } from "../../../containers/reimbursements/UpdateBankInfoModal"
import { useShouldFillOutBankInfo } from "#/components/bank-details/hooks/useGetFundsAdministration"
import { ExpenseType } from "#/utils/ExpenseType"
import { Invoice, useInvoices } from "../../../../hooks/useInvoices"
import { Reimbursement } from "#/types/reimbursement"
import { ReimbursementsListReconciliationExpenseStates } from "#/components/views/reimbursements/reimbursementsList/reimbursement-status-lists/ReimbursementsListReconciliationExpenseStates"

// Custom Elements (Header, Table)
// @ts-expect-error TS(7031) FIXME: Binding element 'children' implicitly has an 'any'... Remove this comment to see the full error message
const Header = ({ children, rightAlign = false }) => (
  <Box
    component="th"
    sx={{
      "&:not(:last-of-type)": {
        paddingInlineEnd: (theme) => theme.tokens.spacing.lg
      }
    }}
  >
    <Typography
      variant="body2"
      color="secondary"
      paddingBottom={(theme) => theme.tokens.spacing.xs}
      align={rightAlign ? "right" : "left"}
    >
      {children}
    </Typography>
  </Box>
)
export const TableHeader = () => {
  const theme = useTheme()
  const showInFullscreen = useMediaQuery(theme.breakpoints.up("sm"))
  if (showInFullscreen) {
    return (
      <>
        <Header>
          <FormattedMessage defaultMessage="Type" description="the category" />
        </Header>
        <Header>
          <FormattedMessage defaultMessage="Submitted on" description="the date the expense was presented" />
        </Header>
        {/* @ts-expect-error TS(2322) FIXME: Type 'string' is not assignable to type 'boolean'. */}
        <Header rightAlign="true">
          <FormattedMessage defaultMessage="Uploads" description="files that have been submitted" />
        </Header>
      </>
    )
  } else {
    return <></>
  }
}

export const Legend = () => {
  const isCarrotCardEligible = useSelector(getIsCarrotCardEligible)
  const { data: reimbursementsList = [] } = useReimbursements()
  const [hasPrevCarrotCardExpenses, setHasPrevCarrotCardExpenses] = useState(false)
  const [hasPrevDirectPayExpenses, setHasPrevDirectPayExpenses] = useState(false)
  const theme = useTheme()

  useEffect(() => {
    reimbursementsList.forEach((x: Reimbursement) => {
      if (x.expenseType === ExpenseType.CARD) {
        if (!hasPrevCarrotCardExpenses) setHasPrevCarrotCardExpenses(true)
      }

      if (x.expenseType === ExpenseType.DIRECT_PAY) {
        if (!hasPrevDirectPayExpenses) setHasPrevDirectPayExpenses(true)
      }
    })
  }, [hasPrevCarrotCardExpenses, hasPrevDirectPayExpenses, reimbursementsList])

  return (
    <Stack direction="row" justifyContent="flex-start" flexWrap="wrap" gap={theme.tokens.spacing.sm}>
      {(isCarrotCardEligible || hasPrevCarrotCardExpenses) && (
        <Box display="flex" flexDirection="row" alignItems="center" gap={theme.tokens.spacing.xs}>
          <Avatar
            sx={{
              inlineSize: 24,
              blockSize: 24,
              bgcolor: theme.palette.brandGreen[200]
            }}
          >
            <CreditCardIcon sx={{ color: theme.palette.brandGreen[700], fontSize: 16 }} />
          </Avatar>
          <Typography variant="body2" color={(theme) => theme.palette.text.secondary}>
            <FormattedMessage defaultMessage="Carrot Card®" />
          </Typography>
        </Box>
      )}
      <Box display="flex" flexDirection="row" alignItems="center" gap={theme.tokens.spacing.xs}>
        <Avatar
          sx={{
            inlineSize: 24,
            blockSize: 24,
            bgcolor: theme.palette.primary.light
          }}
        >
          <BanknoteIcon sx={{ color: theme.palette.primary.accent, fontSize: 16 }} />
        </Avatar>
        <Typography variant="body2" color={(theme) => theme.palette.text.secondary}>
          <FormattedMessage defaultMessage="Reimbursement" description="reimbursement expense category" />
        </Typography>
      </Box>
      {hasPrevDirectPayExpenses && (
        <Box display="flex" flexDirection="row" alignItems="center" gap={theme.tokens.spacing.xs}>
          <Avatar
            sx={{
              inlineSize: 24,
              blockSize: 24,
              bgcolor: theme.palette.brandBlue[200]
            }}
          >
            <HospitalIcon sx={{ color: theme.palette.brandBlue[700], fontSize: 16 }} />
          </Avatar>
          <Typography variant="body2" color={(theme) => theme.palette.text.secondary}>
            <FormattedMessage defaultMessage="Direct pay" />
          </Typography>
        </Box>
      )}
    </Stack>
  )
}

const ExpenseTypeIcon = ({ expenseType }: { expenseType: string }) => {
  const intl = useIntl()
  switch (expenseType) {
    case ExpenseType.CARD:
      return (
        <Box role="img" aria-label={intl.formatMessage({ defaultMessage: "Carrot Card transaction" })}>
          <IconWithBackground icon={CreditCardIcon} variant="circle" size="small" color="green" />
        </Box>
      )
    case ExpenseType.DIRECT_PAY:
      return (
        <Box role="img" aria-label={intl.formatMessage({ defaultMessage: "Direct pay transaction" })}>
          <IconWithBackground icon={HospitalIcon} variant="circle" size="small" color="blue" />
        </Box>
      )
    case ExpenseType.OOP:
      return (
        <Box role="img" aria-label={intl.formatMessage({ defaultMessage: "Reimbursement transaction" })}>
          <IconWithBackground icon={BanknoteIcon} variant="circle" size="small" />
        </Box>
      )
    case ExpenseType.BENEFIT_TRANSFER:
      return (
        <Box role="img" aria-label={intl.formatMessage({ defaultMessage: "Benefit Transfer" })}>
          <IconWithBackground icon={BanknoteIcon} variant="circle" size="small" />
        </Box>
      )
    default:
      return null
  }
}

interface DetailsProps {
  reimbursement: any
  onTransactionClick?: (transaction: any) => void
  invoices?: Invoice[]
  isLoading?: boolean
  isError?: boolean
}

const Details = ({ reimbursement, onTransactionClick, invoices, isLoading, isError }: DetailsProps) => {
  const dateStringToDate = moment(reimbursement.submittedDatetime).toDate()
  const { transactionDetailsDrawerForOopAndCardEnabled } = useFlags()

  // Check expense type
  const isDirectPayExpense = reimbursement.expenseType === ExpenseType.DIRECT_PAY
  const isOopExpense = reimbursement.expenseType === ExpenseType.OOP
  const isCardExpense = reimbursement.expenseType === ExpenseType.CARD

  // Determine if transaction should be clickable
  // DIRECTPAY is always clickable, OOP/CARD are clickable when feature flag is enabled
  const isClickable =
    isDirectPayExpense || (transactionDetailsDrawerForOopAndCardEnabled && (isOopExpense || isCardExpense))

  return (
    <Box sx={{ marginInlineStart: (theme) => theme.spacing(theme.tokens.spacing.xxxs) }}>
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <Box sx={(theme) => ({ flexDirection: "column", marginInlineEnd: theme.spacing(theme.tokens.spacing.md) })}>
          <Typography variant="body2" fontWeight="bold">
            {onTransactionClick && isClickable ? (
              <Link component="button" onClick={() => onTransactionClick(reimbursement)} fontWeight="bold">
                <FormattedDate value={dateStringToDate} year="numeric" month="short" day="2-digit" />
              </Link>
            ) : (
              <FormattedDate value={dateStringToDate} year="numeric" month="short" day="2-digit" />
            )}
          </Typography>
        </Box>
        <ReimbursementAmount reimbursement={reimbursement} />
      </Box>
      <AdjustmentDetails reimbursement={reimbursement} />
      <Typography
        variant="body2"
        color={(theme) => theme.palette.text.tertiary}
        marginBottom={(theme) => theme.tokens.spacing.xxs}
      >
        {reimbursement.merchantName}
      </Typography>
      <Typography variant="body2" color={(theme) => theme.palette.text.tertiary}>
        <RenderExpenseState reimbursement={reimbursement} invoices={invoices} isLoading={isLoading} isError={isError} />
      </Typography>
    </Box>
  )
}

interface RowProps {
  reimbursement: Reimbursement
  onTransactionClick?: (transaction: any) => void
  getInvoiceUrl?: (invoice: Invoice) => void
  invoices?: Invoice[]
  isLoadingInvoices?: boolean
  invoicesError?: any
}

const Row = ({ reimbursement, onTransactionClick, invoices, isLoadingInvoices, invoicesError }: RowProps) => {
  const theme = useTheme()
  const showInFullscreen = useMediaQuery(theme.breakpoints.up("sm"))

  if (showInFullscreen) {
    return (
      <tr {...getPiiProps()} style={{ borderBlockStart: "0.125rem solid rgba(0, 0, 0, 0.05)" }}>
        <td style={{ inlineSize: "3em", paddingBlock: "1rem", verticalAlign: "middle" }}>
          <ExpenseTypeIcon expenseType={reimbursement.expenseType} />
        </td>
        <td style={{ paddingBlock: "1rem", verticalAlign: "middle" }}>
          <Details
            reimbursement={reimbursement}
            onTransactionClick={onTransactionClick}
            invoices={invoices}
            isLoading={isLoadingInvoices}
            isError={invoicesError}
          />
        </td>
        <td style={{ paddingBlock: "1rem" }}>
          <Box sx={{ float: "inline-end" }}>
            {reimbursement.isBenefitTransfer ? (
              <Typography variant="body2" color={(theme) => theme.palette.text.tertiary}>
                <FormattedMessage defaultMessage="Benefit Transfer" />
              </Typography>
            ) : (
              <Attachments reimbursement={reimbursement} />
            )}
          </Box>
        </td>
      </tr>
    )
  } else {
    return (
      <Box
        sx={(theme) => ({ borderBlockStart: `${theme.tokens.borderWidth.md} solid rgba(0, 0, 0, 0.05)` })}
        display="flex"
        flexDirection="row"
        paddingTop={(theme) => theme.spacing(theme.tokens.spacing.md)}
        paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.md)}
      >
        <ExpenseTypeIcon expenseType={reimbursement.expenseType} />
        <Box marginLeft={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
          <Details
            reimbursement={reimbursement}
            onTransactionClick={onTransactionClick}
            invoices={invoices}
            isLoading={isLoadingInvoices}
            isError={invoicesError}
          />
          {reimbursement.isBenefitTransfer ? (
            <Typography variant="body2" color={(theme) => theme.palette.text.tertiary}>
              <FormattedMessage defaultMessage="Benefit Transfer" />
            </Typography>
          ) : (
            <Attachments reimbursement={reimbursement} />
          )}
        </Box>
      </Box>
    )
  }
}

// @ts-expect-error TS(7031) FIXME: Binding element 'reimbursement' implicitly has an ... Remove this comment to see the full error message
const AdjustmentDetails = ({ reimbursement }) => {
  const [isAdjustmentModalOpen, toggleIsAdjustmentsModalOpen] = useToggle(false)
  const intl = useIntl()
  const isDirectPayExpense = reimbursement.expenseType === ExpenseType.DIRECT_PAY
  const AdjustmentInfoModal = () => (
    <EmployeeOnlyModalContainer onClose={toggleIsAdjustmentsModalOpen}>
      <Dialog
        open={isAdjustmentModalOpen}
        aria-labelledby="adjustment-info-modal-title"
        aria-describedby="adjustment-info-modal-description"
        onClose={toggleIsAdjustmentsModalOpen}
      >
        <Box display="flex" justifyContent="flex-end">
          <IconButton
            aria-label={intl.formatMessage({ defaultMessage: "close" })}
            onClick={() => toggleIsAdjustmentsModalOpen()}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogTitle id="adjustment-info-modal-title">
          <FormattedMessage defaultMessage="What does adjusted mean?" />
        </DialogTitle>
        <DialogContent>
          <Box
            sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.lg, marginBlockEnd: theme.tokens.spacing.xxxl })}
          >
            <DialogContentText id="adjustment-info-modal-description">
              <FormattedMessage defaultMessage="We needed to make a change to your expense after we processed it. Reach out to your Care Team with any questions." />
            </DialogContentText>
          </Box>
        </DialogContent>
      </Dialog>
    </EmployeeOnlyModalContainer>
  )
  const dateStringToDate = reimbursement.adjustmentDatetime ? moment(reimbursement.adjustmentDatetime).toDate() : null
  const formattedDate = dateStringToDate
    ? intl.formatDate(dateStringToDate, {
        month: "short",
        day: "2-digit"
      })
    : null
  return reimbursement.adjustmentDatetime ? (
    <>
      <Box sx={{ display: "flex", alignItems: "center" }} marginBottom={(theme) => theme.tokens.spacing.xxs}>
        <Typography variant="body2" color={(theme) => theme.palette.text.tertiary} id="adjustment-date">
          <FormattedMessage defaultMessage="Adjusted on {formattedDate}" values={{ formattedDate }} />
        </Typography>
        {!isDirectPayExpense && (
          <Box
            component="button"
            id="btn-adjustments-more-info"
            sx={{
              marginBlock: 0,
              marginInline: "4px",
              border: "none",
              display: "flex",
              borderRadius: "100%",
              padding: "4px",
              cursor: "pointer",
              backgroundColor: (theme) => theme.palette.common.white,
              "&:hover, &:focus": { backgroundColor: "#f9ead5" }
            }}
            aria-label={intl.formatMessage({
              defaultMessage: "Learn more about adjusted expense"
            })}
            onClick={() => toggleIsAdjustmentsModalOpen()}
          >
            <Icon name="helpCircle" color="carrotOrange" size="15px" />
          </Box>
        )}
      </Box>
      {isAdjustmentModalOpen && <AdjustmentInfoModal />}
    </>
  ) : null
}

// Components Shared Between Larger Components
// @ts-expect-error TS(7031) FIXME: Binding element 'subsidy' implicitly has an 'any' ... Remove this comment to see the full error message
export const ReimbursementSummary = ({ subsidy, reimbursementsCount, showRemainingAnnual, showRemainingLifetime }) => {
  const intl = useIntl()
  const lifetimeAvailable = subsidy.lifetimeAmounts.available
  const yearlyAvailable = subsidy.yearlyAmounts.available
  const used = subsidy.lifetimeAmounts.claimed
  const companyCurrencyCode = useSelector(getCompanyCurrencyCode)

  return (
    <ReimbursementDescriptionList>
      {showRemainingAnnual && (
        <ReimbursementDescriptionGroup
          title={intl.formatMessage({
            defaultMessage: "Remaining annual",
            description: "the remaining amount of money allocated for the year"
          })}
          value={
            subsidy.hasUnlimitedBenefit
              ? intl.formatMessage({ defaultMessage: "Unlimited" })
              : intl.formatNumber(Math.max(yearlyAvailable / 100, 0), {
                  style: "currency",
                  currency: companyCurrencyCode,
                  currencyDisplay: "narrowSymbol",
                  maximumFractionDigits: 0
                })
          }
        />
      )}
      {showRemainingLifetime && (
        <ReimbursementDescriptionGroup
          title={intl.formatMessage({
            defaultMessage: "Remaining lifetime",
            description: "the remaining amount of money allocated for lifetime"
          })}
          value={
            subsidy.hasUnlimitedBenefit || subsidy.lifetimeAmounts.max === null
              ? intl.formatMessage({ defaultMessage: "Unlimited" })
              : intl.formatNumber(Math.max(lifetimeAvailable / 100, 0), {
                  style: "currency",
                  currency: companyCurrencyCode,
                  currencyDisplay: "narrowSymbol",
                  maximumFractionDigits: 0
                })
          }
        />
      )}
      <ReimbursementDescriptionGroup
        title={intl.formatMessage({
          defaultMessage: "Used",
          description: "money spent"
        })}
        value={intl.formatNumber(Math.max(used / 100, 0), {
          style: "currency",
          currency: companyCurrencyCode,
          currencyDisplay: "narrowSymbol",
          maximumFractionDigits: 0
        })}
      />
      <ReimbursementDescriptionGroup
        title={intl.formatMessage({ defaultMessage: "# of expenses", description: "number of expenses" })}
        value={intl.formatNumber(reimbursementsCount)}
      />
    </ReimbursementDescriptionList>
  )
}

interface ReimbursementsListSectionProps {
  title: React.ReactNode
  items: any[]
  emptyText?: string
  showAlert?: boolean
  onTransactionClick?: (transaction: any) => void
}

export const ReimbursementsListSection = ({
  title,
  items,
  emptyText = "",
  showAlert,
  onTransactionClick
}: ReimbursementsListSectionProps) => {
  const hasItems = items && items.length > 0
  const theme = useTheme()

  const areAnyReconciliationsExpensesCC = items.some(
    (item) => item.expenseState === ExpenseState.DENIED_RECONCILIATION_REQUESTED
  )

  const reconciliationExpensesForUseInvoices = useMemo(
    () =>
      items
        .map((expense) =>
          expense.hasBeenReconciled
            ? expense.reconciliationRequestedExpenseId
              ? expense.reconciliationRequestedExpenseId
              : expense.expenseId
            : null
        )
        .filter((expenseId) => expenseId !== null),
    [items]
  )

  const invoicesForAllReconciliationExpenses = useInvoices(reconciliationExpensesForUseInvoices)

  const invoicesByExpenseIdDataMap = useMemo(() => {
    return reconciliationExpensesForUseInvoices.reduce((acc, expenseId, index) => {
      const queryResult = invoicesForAllReconciliationExpenses[index]
      acc[expenseId] = {
        invoices: queryResult?.data || [],
        isLoading: queryResult?.isLoading || false,
        error: queryResult?.error || null
      }
      return acc
    }, {} as Record<number, { invoices: Invoice[]; isLoading: boolean; error: any }>)
  }, [reconciliationExpensesForUseInvoices, invoicesForAllReconciliationExpenses])

  const openInvoices = useMemo(() => {
    const newOpenInvoices: Invoice[] = []

    for (const [, value] of Object.entries(invoicesByExpenseIdDataMap)) {
      // set up type
      const invoiceListValueType = value as { invoices: Invoice[]; isLoading: boolean; error: any }

      if (invoiceListValueType.invoices.some((invoice: Invoice) => invoice.status === "open")) {
        const openInvoice = invoiceListValueType.invoices.find((invoice: Invoice) => invoice.status === "open")
        if (openInvoice) {
          newOpenInvoices.push(openInvoice)
        }
      }
    }
    return newOpenInvoices
  }, [invoicesByExpenseIdDataMap])

  const hasOpenInvoices = openInvoices.length > 0

  return (
    <div>
      <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
        <Typography variant="h4" textTransform="uppercase">
          {title}
        </Typography>
      </Box>
      <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
        {showAlert && hasOpenInvoices ? (
          <Alert severity="error" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.lg })}>
            <Stack display="flex" flexDirection="column" spacing={theme.tokens.spacing.sm}>
              <Box>
                {areAnyReconciliationsExpensesCC ? (
                  <FormattedMessage defaultMessage="You owe a reconciliation payment. Please make a payment to unlock your Carrot Card." />
                ) : (
                  <FormattedMessage defaultMessage="You owe a reconciliation payment. Please check your email for details." />
                )}
              </Box>
              <Box>
                <FormattedMessage
                  defaultMessage="<link>Make payment</link>"
                  values={{
                    link: (linkContent) => (
                      <Button
                        sx={(theme) => ({ backgroundColor: theme.palette.background.default })}
                        component="a"
                        color="secondary"
                        variant="outlined"
                        href={openInvoices[0]?.invoicePaymentUrl}
                        target="_blank"
                      >
                        {linkContent}
                      </Button>
                    )
                  }}
                />
              </Box>
            </Stack>
          </Alert>
        ) : null}
        {showAlert && items.find((item) => item.expenseState === ExpenseState.NEEDS_MEMBER_UPLOADS) ? (
          <Alert severity="warning" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.lg })}>
            <FormattedMessage
              defaultMessage="We need additional documents to process your expense. Please <link>check your messages</link> for details."
              values={{
                link: (linkContent) => (
                  <Link color="inherit" href={"/messages"}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Alert>
        ) : null}
        <table style={{ inlineSize: "100%", borderCollapse: "collapse", borderSpacing: 0 }}>
          <thead style={hasItems ? undefined : { opacity: 0.4 }}>
            <tr>
              <TableHeader />
            </tr>
          </thead>
          <tbody>
            {hasItems ? (
              items.map((item) => {
                const expenseId = item.hasBeenReconciled
                  ? item.reconciliationRequestedExpenseId || item.expenseId
                  : item.expenseId
                const invoiceData = invoicesByExpenseIdDataMap[expenseId] || {
                  invoices: [],
                  isLoading: false,
                  error: null
                }

                return (
                  <Row
                    key={item.expenseId}
                    reimbursement={item}
                    onTransactionClick={onTransactionClick}
                    invoices={invoiceData.invoices}
                    isLoadingInvoices={invoiceData.isLoading}
                    invoicesError={invoiceData.error}
                  />
                )
              })
            ) : (
              <tr>
                <td
                  colSpan={3}
                  style={{ borderBlockStart: "0.125rem solid rgba(0, 0, 0, 0.05)", paddingBlock: "1rem" }}
                >
                  <Typography variant="body2" color={(theme) => theme.palette.text.tertiary} textAlign="center">
                    {emptyText}
                  </Typography>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
    </div>
  )
}

// @ts-expect-error TS(7031) FIXME: Binding element 'reimbursement' implicitly has an ... Remove this comment to see the full error message
const NeedsMemberUploadsPendingView = ({ reimbursement }) => {
  const intl = useIntl()
  const [isUploadAdditionalDocumentsDialogOpen, setIsUploadAdditionalDocumentsDialogOpen] = useState(false)
  const [isUpdateBankInfoModalOpen, toggleIsUpdateBankInfoModalOpen] = useToggle(false)
  const { refreshUserInfo } = useCurrentUser()
  const shouldShowBankInfoModal = useShouldFillOutBankInfo()

  // @ts-expect-error TS(7006) FIXME: Parameter 'event' implicitly has an 'any' type.
  const toggleUploadAdditionalDocumentsModal = (event) => {
    event.preventDefault()
    if (shouldShowBankInfoModal?.data && reimbursement.expenseType !== ExpenseType.CARD) {
      toggleIsUpdateBankInfoModalOpen()
    } else {
      setIsUploadAdditionalDocumentsDialogOpen(true)
    }
  }

  const onClose = () => {
    refreshUserInfo()
    setIsUploadAdditionalDocumentsDialogOpen(false)
  }

  const requestedDate = moment(reimbursement.currentExpenseStateCreatedDate).toDate()
  const formattedRequestedDate = intl.formatDate(requestedDate, {
    month: "short",
    day: "2-digit"
  })
  return (
    <Stack color={(theme) => theme.palette.secondary.main}>
      <Typography variant="body2">
        <FormattedMessage
          defaultMessage="Documents requested {formattedRequestedDate}"
          values={{ formattedRequestedDate }}
        />
      </Typography>
      <Link
        color="inherit"
        component="button"
        variant="body2"
        alignSelf="flex-start"
        sx={{ marginBlockStart: "5px" }}
        onClick={toggleUploadAdditionalDocumentsModal}
      >
        <FormattedMessage defaultMessage="Add documents" />
      </Link>
      {/* @ts-expect-error TS(2741) FIXME: Property 'onNavigateToAccountPage' is missing in t... Remove this comment to see the full error message */}
      {isUpdateBankInfoModalOpen && <UpdateBankInfoModal onClose={toggleIsUpdateBankInfoModalOpen} />}
      {isUploadAdditionalDocumentsDialogOpen && (
        <UploadAdditionalDocumentsFlow
          expenseId={reimbursement.expenseId}
          onExit={onClose}
          isOpen={isUploadAdditionalDocumentsDialogOpen}
        />
      )}
    </Stack>
  )
}

interface RenderExpenseStateProps {
  reimbursement: any
  invoices?: Invoice[]
  isLoading?: boolean
  isError?: boolean
}

const RenderExpenseState = ({ reimbursement, invoices, isLoading, isError }: RenderExpenseStateProps) => {
  const intl = useIntl()
  const submitByDate = moment(reimbursement.submittedDatetime).toDate()
  const theme = useTheme()
  // Set "Upload Statement By" date
  submitByDate.setDate(submitByDate.getDate() + 30)
  const dateStringToDate = moment(submitByDate).toDate()
  const formattedDate = intl.formatDate(dateStringToDate, {
    month: "short",
    day: "2-digit"
  })

  switch (reimbursement.expenseType) {
    // Carrot Card
    case ExpenseType.CARD:
      switch (reimbursement.expenseState) {
        case ExpenseState.PENDING_MEMBER_INFO:
          return (
            <FormattedMessage
              defaultMessage="Please upload an itemized statement by {formattedDate}"
              values={{ formattedDate }}
            />
          )
        case ExpenseState.NEEDS_MEMBER_UPLOADS:
          return <NeedsMemberUploadsPendingView reimbursement={reimbursement} />
        case ExpenseState.APPROVED:
          return (
            <Stack spacing={theme.tokens.spacing.xxs}>
              {invoices?.some((invoice) => invoice.status === "paid") ? (
                <ReimbursementsListReconciliationExpenseStates
                  isLoading={isLoading}
                  isError={isError}
                  invoices={invoices}
                />
              ) : (
                <FormattedMessage defaultMessage="Approved" />
              )}
            </Stack>
          )
        case ExpenseState.DENIED_RECONCILED:
          return (
            <Stack spacing={theme.tokens.spacing.xxs}>
              {invoices?.some((invoice) => invoice.status === "paid") && (
                <ReimbursementsListReconciliationExpenseStates
                  isLoading={isLoading}
                  isError={isError}
                  invoices={invoices}
                />
              )}
            </Stack>
          )
        case ExpenseState.DENIED_RECONCILIATION_REQUESTED:
          return (
            <Stack spacing={theme.tokens.spacing.xxs}>
              {invoices?.some((inv) => inv.status === "open" || inv.status === "paid") && (
                <ReimbursementsListReconciliationExpenseStates
                  isLoading={isLoading}
                  isError={isError}
                  invoices={invoices}
                />
              )}
            </Stack>
          )
        case ExpenseState.ON_HOLD:
        case ExpenseState.PENDING_CARE_NAV:
          return <FormattedMessage defaultMessage="In progress" />
        default:
          reportErrorMessage(`Unexpected expense state provided for card expense: ${reimbursement.expenseState}`)
          return reimbursement.expenseState
      }
    // Out of pocket
    case ExpenseType.OOP:
      switch (reimbursement.expenseState) {
        case ExpenseState.ON_HOLD:
        case ExpenseState.RECEIVED:
          return <FormattedMessage defaultMessage="In progress" />
        case ExpenseState.NEEDS_MEMBER_UPLOADS:
          return <NeedsMemberUploadsPendingView reimbursement={reimbursement} />
        case ExpenseState.INVOICING:
        case ExpenseState.INVOICED:
        case ExpenseState.APPROVED:
          return (
            <Stack spacing={theme.tokens.spacing.xxs}>
              <FormattedMessage
                defaultMessage="Approved on {approvedDate}"
                values={{
                  approvedDate: intl.formatDate(moment(reimbursement.approvalDatetime).toDate(), {
                    month: "short",
                    day: "2-digit"
                  })
                }}
              />
              <ReimbursementsListDeductibleString reimbursement={reimbursement} />
              {invoices?.some((invoice) => invoice.status === "paid") && (
                <ReimbursementsListReconciliationExpenseStates
                  isLoading={isLoading}
                  isError={isError}
                  invoices={invoices}
                />
              )}
            </Stack>
          )
        case ExpenseState.PAID:
          return reimbursement.reconciliationRequestedExpenseId ? (
            <Stack spacing={theme.tokens.spacing.xxs}>
              {invoices?.some((inv) => inv.status === "open" || inv.status === "paid") && (
                <ReimbursementsListReconciliationExpenseStates
                  isLoading={isLoading}
                  isError={isError}
                  invoices={invoices}
                />
              )}
            </Stack>
          ) : (
            <Stack spacing={theme.tokens.spacing.xxs}>
              <ReimbursementsListPaidExpenseState reimbursement={reimbursement} />
              {invoices?.some((inv) => inv.status === "paid") && (
                <ReimbursementsListReconciliationExpenseStates
                  isLoading={isLoading}
                  isError={isError}
                  invoices={invoices}
                />
              )}
            </Stack>
          )
        case ExpenseState.DENIED:
          return (
            <Stack spacing={theme.tokens.spacing.xxs}>
              <FormattedMessage defaultMessage="Denied" />
              {invoices?.some((invoice) => invoice.status === "paid") && (
                <ReimbursementsListReconciliationExpenseStates
                  isLoading={isLoading}
                  isError={isError}
                  invoices={invoices}
                />
              )}
            </Stack>
          )
        default:
          reportErrorMessage(`Unexpected expense state provided for OOP expense: ${reimbursement.expenseState}`)
          return reimbursement.expenseState
      }
    // Direct Pay
    case ExpenseType.DIRECT_PAY:
      switch (reimbursement.expenseState) {
        case ExpenseState.PENDING_NEXT_PLAN_YEAR:
        case ExpenseState.ON_HOLD:
        case ExpenseState.RECEIVED:
        case ExpenseState.INVOICING:
        case ExpenseState.INVOICED:
        case ExpenseState.APPROVED:
        case ExpenseState.BILL_SUBMITTED:
          return <FormattedMessage defaultMessage="In progress" />
        case ExpenseState.NEEDS_PROVIDER_INFO:
          return <FormattedMessage defaultMessage="On hold with provider" />
        case ExpenseState.SCHEDULED:
          return <FormattedMessage defaultMessage="Scheduled" />
        case ExpenseState.DENIED:
          return <FormattedMessage defaultMessage="Denied" />
        case ExpenseState.CANCELLED:
          return <FormattedMessage defaultMessage="Canceled" />
        case ExpenseState.PAID:
          return <ReimbursementsListPaidExpenseState reimbursement={reimbursement} />
        default:
          reportErrorMessage(`Unexpected expense state provided for Direct Pay expense: ${reimbursement.expenseState}`)
          return reimbursement.expenseState
      }
    case ExpenseType.BENEFIT_TRANSFER:
      return <FormattedMessage defaultMessage="Completed" />
    default:
      reportErrorMessage(`Unexpected expense type provided: ${reimbursement.expenseType}`)
      return reimbursement.expenseType
  }
}
